import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Building2, Plus, Users, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { useToast } from '../../hooks/use-toast';

const OrganizationSwitcher = ({ showCreateOption = true, className = "" }) => {
  const { 
    currentOrganization, 
    organizations, 
    switchOrganization, 
    createOrganization,
    user 
  } = useAuth();
  const { toast } = useToast();
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: ''
  });
  const [creating, setCreating] = useState(false);

  const handleOrganizationSwitch = async (organizationId) => {
    const result = await switchOrganization(organizationId);
    if (result.success) {
      const org = organizations.find(o => o.id === organizationId);
      toast({
        title: "Organization switched",
        description: `Now working in ${org?.name}`,
      });
    } else {
      toast({
        title: "Error",
        description: result.error,
        variant: "destructive",
      });
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const result = await createOrganization(createForm.name, createForm.description);
      
      if (result.success) {
        toast({
          title: "Organization created",
          description: `${result.organization.name} has been created successfully`,
        });
        setShowCreateDialog(false);
        setCreateForm({ name: '', description: '' });
      } else {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create organization",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  if (organizations.length === 0) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <Building2 className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">No organizations</span>
        {showCreateOption && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="w-3 h-3 mr-1" />
                Create
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Organization</DialogTitle>
                <DialogDescription>
                  Create a new organization to manage your events and team.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateOrganization} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                    placeholder="Enter organization name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="orgDescription">Description (Optional)</Label>
                  <Textarea
                    id="orgDescription"
                    value={createForm.description}
                    onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                    placeholder="Describe your organization"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowCreateDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? 'Creating...' : 'Create Organization'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  if (organizations.length === 1) {
    const org = organizations[0];
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <Building2 className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-900">{org.name}</span>
        <span className="text-xs text-gray-500">({org.slug})</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <Building2 className="w-4 h-4 text-gray-600" />
      <Select 
        value={currentOrganization?.id || ''} 
        onValueChange={handleOrganizationSwitch}
      >
        <SelectTrigger className="w-48">
          <SelectValue>
            {currentOrganization ? (
              <div className="flex items-center space-x-2">
                <span className="font-medium">{currentOrganization.name}</span>
                <span className="text-xs text-gray-500">({currentOrganization.slug})</span>
              </div>
            ) : (
              "Select organization..."
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {organizations.map((org) => (
            <SelectItem key={org.id} value={org.id}>
              <div className="flex items-center justify-between w-full">
                <div>
                  <div className="font-medium">{org.name}</div>
                  <div className="text-xs text-gray-500">{org.slug}</div>
                </div>
                {org.owner_id === user?.id && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">Owner</span>
                )}
              </div>
            </SelectItem>
          ))}
          {showCreateOption && (
            <SelectItem value="__create_new__" onSelect={() => setShowCreateDialog(true)}>
              <div className="flex items-center space-x-2 text-blue-600">
                <Plus className="w-3 h-3" />
                <span>Create new organization</span>
              </div>
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      
      {showCreateOption && (
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Organization</DialogTitle>
              <DialogDescription>
                Create a new organization to manage your events and team.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateOrganization} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orgName">Organization Name</Label>
                <Input
                  id="orgName"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  placeholder="Enter organization name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="orgDescription">Description (Optional)</Label>
                <Textarea
                  id="orgDescription"
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  placeholder="Describe your organization"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Organization'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default OrganizationSwitcher;